import isNil from 'lodash/isNil';
import { LabelProps } from 'recharts';
import { CartesianViewBox } from 'recharts/types/util/types';
import { LabelPosition } from 'recharts/types/component/Label';
import { getTextWidth } from '../utils';
import { IDisplayedMeasure } from '../interfaces';
import { theme } from '../../../styles/theme';
import useYAxisWidth from '../hooks/useYAxisWidth';

interface IProps {
  props: LabelProps;
  chartWidth: number;
  displayedMeasures: IDisplayedMeasure[];
}

const getCoordinates = (
  position: LabelPosition | undefined,
  chartWidth: number,
  viewBox: CartesianViewBox,
) => {
  const yValue = !isNil(viewBox?.y) ? viewBox.y : 0;

  const x = !isNil(viewBox?.height) ? viewBox.height / 2 + yValue : 85.5;

  if (position === 'left') {
    return {
      x: x * -1,
      y: 12.5,
    };
  }

  return {
    x,
    y: (chartWidth - 58) * -1,
  };
};

const getText = (str: string): string => {
  const currentTextWidth = getTextWidth(str, '14pt bold');

  if (currentTextWidth <= 150) {
    return str;
  }

  const singleCharLength = currentTextWidth / str.length;

  const delta = currentTextWidth - 150;
  const charactersNeededToTrim = Math.ceil(delta / singleCharLength) + 3; // 3 For the ellipsis;

  const totalLengthAfterTrim = str.length - charactersNeededToTrim;

  return `${str.slice(0, totalLengthAfterTrim)}...`;
};

export default ({ props, chartWidth, displayedMeasures }: IProps) => {
  const { position, fontWeight, angle, viewBox, value } = props;

  // if viewBox is not a CartesianViewBox, return null
  // PolarViewBox only works in PieChart
  if (!viewBox || !('height' in viewBox)) {
    return null;
  }
  const { x, y } = getCoordinates(position, chartWidth, viewBox);

  const resultMeasure = displayedMeasures.find(
    (measure) => measure.isResultKey,
  );
  const shouldHideMeasureLabel = resultMeasure && displayedMeasures.length > 1;
  const onlyShowResultMeasureLabel =
    resultMeasure && displayedMeasures.length === 1;

  if (shouldHideMeasureLabel) {
    return <></>;
  }

  // Calculate spacing for right y axis label adjustment to prevent overlapping
  const yAxisWidth = useYAxisWidth({
    label: value && onlyShowResultMeasureLabel ? value.toString() : '',
    padding: -8,
  });

  return (
    <>
      {onlyShowResultMeasureLabel ? (
        <text
          dy={position === 'right' ? -yAxisWidth : 0}
          y={y}
          x={x}
          stroke="none"
          fill={theme.colors.mainBlue}
          fontWeight={fontWeight}
          textAnchor="middle"
          transform={`rotate(${angle}, 0, 0)`}
        >
          <tspan>{getText(displayedMeasures[0].name)}</tspan>
        </text>
      ) : (
        displayedMeasures.map((measure, index) => {
          const viewBoxY = !isNil(viewBox?.y) ? viewBox.y : 0;
          // adjust x from the chart YAxis to the display position
          const viewBoxX = !isNil(viewBox?.x) ? viewBox.x : 0;
          const calculatedX = viewBoxX + (position === 'left' ? 12 : 60);
          // adjust labels position from visibleMeasures.length
          const calculatedY = !isNil(viewBox?.height)
            ? viewBox.height / 2 + viewBoxY - displayedMeasures.length * 4
            : 85.5;

          return (
            <text
              key={measure.key + index}
              y={calculatedY + index * 26}
              x={calculatedX}
              stroke="none"
              fill={theme.colors.mainBlue}
              fontWeight={fontWeight}
              textAnchor="middle"
            >
              <tspan>{measure.label}</tspan>
            </text>
          );
        })
      )}
    </>
  );
};
